import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {RoundStatus} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {findIndex, includes} from "lodash";

export interface IRound {
	id: number;
	status: RoundStatus;
	date: string;
}
export interface IRoundsStore {
	get list(): IRound[];
	get availableRounds(): IRound[];
	get activeRound(): IRound | undefined;
	get currentRound(): IRound | undefined;
	get selectedRoundId(): number | null;
	get selectedIndex(): number;

	fetchRounds(): Promise<void>;
	setSelectedRoundId(roundId: number): void;
}

@injectable()
export class RoundsStore implements IRoundsStore {
	@observable private _list: IRound[] = [];
	@observable private _selectedRoundId: number | null = null;

	get list() {
		return this._list;
	}

	get activeRound() {
		return this._list.find((round) => round.status === RoundStatus.LIVE);
	}

	get selectedRoundId() {
		return this._selectedRoundId;
	}

	get currentRound() {
		return this._list.find((round) => round.id === this._selectedRoundId);
	}

	get availableRounds() {
		return this._list.filter((round) =>
			includes([RoundStatus.COMPLETED, RoundStatus.LIVE], round.status)
		);
	}

	get selectedIndex() {
		return findIndex(this.availableRounds, (round) => round.id === this.selectedRoundId);
	}

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	@action setSelectedRoundId(roundId: number) {
		this._selectedRoundId = roundId;
	}

	@action
	async fetchRounds() {
		const {data} = await this._jsonProvider.rounds();

		runInAction(() => {
			this._list = data;
		});
	}
}
